import { useEffect, useRef, useState, useCallback, useMemo } from 'react'
import {
  PatchDispatchTicketWithTagMetadata,
  useGetDispatchTicketQuery,
  usePatchDispatchTicketMutation,
} from 'services/dispatch'
import { useGetPriceSheetQuery } from 'services/priceSheets'
import { useGetCustomerQuery } from 'services/customers'
import { v4 as uuidv4 } from 'uuid'
import {
  ASSET_CATEGORY,
  DEFAULT_PRECISION,
  DISPATCH_TICKET_ENUM,
  DISPATCH_TICKET_TYPES,
  DT_STATUS_ENUM,
  GS_ERROR_CODE,
  ROUNDING_PRECISIONS,
} from 'utils/constants'
import { dispatchSnackbar } from 'utils/helpers'
import { useCreateOutboundTicketMutation, useUpdateOutboundTicketMutation } from 'services/outboundTickets.js'
import { useDispatch, useSelector } from 'react-redux'
import Dinero from 'dinero.js'
import { DispatchTicketSchema } from 'gen/rtk-openapi.ts'
import { roundTo } from 'utils/data-manipulation/valueFormatter'
import { useCalculateFreightCost } from 'utils/custom-hooks/useCalculateFreightCost'
import { getNetValue } from 'utils/data-manipulation/common'
import { useAppSelector } from '../../../../../utils/custom-hooks/useTypedReduxStore.ts'

const useLinkDispatchToObt = ({
  selectedDispatchTicketId,
  onObtCreated,
  formattedObtData,
  outboundTicketData,
  cachedMaterialTypesData,
  formData,
  salesOrderData,
  carriersData,
}) => {
  const { enableSalesOrderFreightCosts } = useAppSelector(state => state.featureFlags?.variables || {})
  const { consumerInfo: consumerInfoState } = useSelector(state => (state as any).outboundTicket)

  const { data: dispatchTicketDetails, isFetching: isFetchingDtDetails } = useGetDispatchTicketQuery(
    { dispatchTicketId: selectedDispatchTicketId },
    { skip: !selectedDispatchTicketId },
  )

  const carrierData = useMemo(() => {
    if (!dispatchTicketDetails?.driver?.carrierId || !carriersData?.Items?.length) {
      return null
    }

    return carriersData.Items.find(carrier => carrier.id === dispatchTicketDetails?.driver?.carrierId) || null
  }, [dispatchTicketDetails, carriersData?.Items])

  const { data: customerData, isFetching: isFetchingCustomerData } = useGetCustomerQuery(
    { customerId: dispatchTicketDetails?.customerId },
    { skip: !dispatchTicketDetails?.customerId },
  )

  const { data: priceSheetData, isLoading: isLoadingPriceSheetData } = useGetPriceSheetQuery(
    { priceSheetId: customerData?.priceSheetId },
    { skip: !customerData?.priceSheetId },
  )

  const [userDidConfirm, setUserDidConfirm] = useState(false)
  const idempotencyKey = useRef(uuidv4())
  const [patchDispatchTicket] = usePatchDispatchTicketMutation()
  const [createOutboundTicket, { data: obtCreateData, isSuccess: obtCreateSuccess }] = useCreateOutboundTicketMutation()
  const [updateOutboundTicket, { data: obtUpdateData, isSuccess: obtUpdateSuccess }] = useUpdateOutboundTicketMutation()
  const dispatch = useDispatch()
  const { locationId: yardLocationId } = useSelector(state => (state as any).settings.details)
  const { defaultCurrency, defaultYardUm } = useSelector(state => (state as any).settings)

  /**
   * Returns the updated OBT material list with the new priceList from the DT that is being linked
   * - Unused for now. Left in for future use to keep similarity to IBT DT linking -
   * @param dt
   */
  const getUpdatedMaterialsList = () => {
    if (formData?.materials?.length === 0) return []
    const formattedMaterials = formData?.materials.map(material => {
      const gross = Number(material?.weightAndPrice?.gross?.amount || 0)
      const tare = Number(material?.weightAndPrice?.tare?.amount || 0)
      const deductions = material?.weightAndPrice?.deductions || []
      const defaultPriceEntry = cachedMaterialTypesData?.[material.materialTypeId]?.defaultPriceEntry
      const supplementalPriceEntry = priceSheetData?.entriesObj?.find(
        entry => entry.materialTypeId === material.materialTypeId,
      )
      const priceEntry = supplementalPriceEntry || defaultPriceEntry
      const newPrice = priceEntry?.buyPrice?.price
      const newUnit = priceEntry?.weightUnits || defaultYardUm
      const newNetValue = getNetValue({
        gross,
        tare,
        price: Dinero({ ...newPrice }).toUnit(),
        deductions,
        fromUnit: defaultYardUm,
        toUnit: newUnit,
      })
      return {
        ...material,
        weightAndPrice: {
          ...(material?.weightAndPrice || {}),
          ...(newPrice
            ? {
                netPrice: newPrice,
                customerPreferredPrice: newPrice,
                um: newUnit,
                customerPreferredUm: newUnit,
                netValue: {
                  amount: roundTo(newNetValue * 10 ** DEFAULT_PRECISION),
                  currency: defaultCurrency,
                  precision: DEFAULT_PRECISION,
                },
              }
            : {}),
        },
      }
    })
    return formattedMaterials
  }

  /**
   * creates a partial OBT payload to be added to a dispatch ticket
   * This only adds the DT fields
   * @param dt {Object} - api response model of GEt dispatch ticket by id
   */
  const mapDtToObt = (dt: DispatchTicketSchema) => {
    const assetNamesArray: DispatchTicketSchema['dispatchTicketAssets'] = []
    // determine assets coming into yard which are containers

    dt?.dispatchTicketAssets?.forEach(dtAsset => {
      if (
        // if delivery or dropoff, we include all containers
        (dt.type === DISPATCH_TICKET_TYPES.DELIVERY.id || dt.type === DISPATCH_TICKET_TYPES.DROP_OFF.id) &&
        dtAsset.asset.assetCategory === ASSET_CATEGORY.CONTAINER
      ) {
        assetNamesArray.push(dtAsset.asset.assetName)
        // if exchange, we include only containers heading outbound
      } else if (
        dtAsset.fromLocationId === yardLocationId &&
        dtAsset.asset.assetCategory === ASSET_CATEGORY.CONTAINER
      ) {
        assetNamesArray.push(dtAsset.asset.assetName)
      }
    })
    const assetNames = assetNamesArray.length > 0 ? assetNamesArray.join(', ') : null

    let vehicleId = null
    if (dt.customerId === outboundTicketData?.customerId) {
      vehicleId = outboundTicketData?.vehicleId
    }

    // Returns the data for use on the OBT:
    return {
      transportationInfo: {
        ...formattedObtData?.transportationInfo,
        carrier: dt?.driver?.name ? dt.driver.name : '',
        container: assetNames || '',
        trailerNumber:
          dt?.dispatchTicketAssets?.find(dtAsset => dtAsset.asset.assetCategory === ASSET_CATEGORY.TRAILER)?.asset
            ?.assetName || '',
        carrierNumber:
          dt?.dispatchTicketAssets?.find(dtAsset => dtAsset.asset.assetCategory === ASSET_CATEGORY.TRUCK)?.asset
            ?.assetName || '',
        carrierLookup: carrierData
          ? {
              id: carrierData?.id || '',
              customerCommonIdentifierString: carrierData?.customerCommonIdentifierString || '',
              displayName: carrierData?.displayName || '',
            }
          : null,
      },
      customerId: dt.customerId,
      vehicleId: vehicleId,
      carrierId: dt?.driver?.carrierId ? dt.driver.carrierId : outboundTicketData?.carrierId,
    }
  }

  const { isLoading: isLoadingFreightLanesData, priorityFreightCostInfo } = useCalculateFreightCost({
    location: customerData?.locations?.find(location => location.id === dispatchTicketDetails?.locationId),
    transportationType: formData?.transportationInfo?.transportationType,
    carrierId: formData?.carrierId,
    effectiveDate: formData?.effectiveDate,
    netWeight: formData?.netWeight || 0,
    ticketType: 'OBT',
    contract: enableSalesOrderFreightCosts && consumerInfoState?.salesOrderId ? salesOrderData : null,
  })

  const generateEnhancedFormData = ({ formData, priorityFreightCostInfo }) => {
    const { userSet, userChargebackSet } = formData?.freightCost || {}
    const oldChargeback = formData.freightCost?.chargeBack || 0
    const newChargeback = priorityFreightCostInfo?.chargeBack ?? 0

    // CHANGE: priorityFC.

    const newFormData = {
      ...formData,
      freightCost: {
        ...formData.freightCost,
        ...(!userSet && { cost: priorityFreightCostInfo?.freightCost }),
        ...(!userChargebackSet && { chargeBack: newChargeback }),
      },
      ...(!userChargebackSet && {
        netValue: roundTo(formData.netValue + oldChargeback - newChargeback, ROUNDING_PRECISIONS.COST),
      }),
      shippingInfo: {
        ...formData.shippingInfo,
        address: formData.shippingInfo?.shippingAddress || '',
      },
    }

    delete newFormData.shippingInfo.shippingAddress

    return newFormData
  }

  const createObt = async () => {
    const obtNewData = mapDtToObt(<DispatchTicketSchema>dispatchTicketDetails)
    const enhancedFormData = generateEnhancedFormData({
      formData: formattedObtData,
      priorityFreightCostInfo,
    })

    const obtPayload = {
      ...formattedObtData,
      ...enhancedFormData,
      ...obtNewData,
    }

    const createObtRes = await createOutboundTicket({
      outboundTicketRequestDto: obtPayload,
    })
    if (createObtRes.error) {
      dispatchSnackbar(dispatch, 'error', 'error creating outbound ticket')
    }
  }

  const updateObt = async () => {
    const obtNewData = mapDtToObt(<DispatchTicketSchema>dispatchTicketDetails)
    const enhancedFormData = generateEnhancedFormData({
      formData: formattedObtData,
      priorityFreightCostInfo,
    })

    // Prioritize the DT data over the unsaved form data over the obt data:
    const obtPayload = {
      ...formattedObtData,
      ...enhancedFormData,
      ...obtNewData,
    }
    delete obtPayload.netPrice

    const updateObtRes = await updateOutboundTicket({
      outboundTicketId: outboundTicketData?.id,
      outboundTicketRequestDto: obtPayload,
    })

    if (updateObtRes.error) {
      dispatchSnackbar(dispatch, 'error', 'error updating outbound ticket')
    }
  }

  /**
   * In case dispatch ticket details are still being fetched, we'll need to pause until they come back
   * then we can re-trigger the createObt function
   */
  useEffect(
    function onDataReady() {
      if (
        !isFetchingDtDetails &&
        !isFetchingCustomerData &&
        !isLoadingFreightLanesData &&
        !isLoadingPriceSheetData &&
        userDidConfirm
      ) {
        if (outboundTicketData?.id) {
          updateObt()
        } else {
          createObt()
        }
      }
    },
    [isFetchingDtDetails, isFetchingCustomerData, isLoadingFreightLanesData, isLoadingPriceSheetData, userDidConfirm],
  )

  useEffect(() => {
    if (!obtCreateSuccess && !obtUpdateSuccess) return

    const obtData = outboundTicketData?.id ? obtUpdateData : obtCreateData

    onObtCreated(obtData)

    // get asset ids from dispatch ticket so that asset IDs can be invalidated
    const assetIds = dispatchTicketDetails?.dispatchTicketAssets?.map(asset => asset.id)
    const updateDispatchTicket = async () => {
      const dtUpdatePayload: PatchDispatchTicketWithTagMetadata = {
        dispatchTicketId: selectedDispatchTicketId,
        outboundTicketId: obtData.id,
        assetIds: (assetIds as string[]) || [],
        patchDispatchTicketBody: {
          outboundTicketId: outboundTicketData?.id ? outboundTicketData.id : obtData.id,
          hash: dispatchTicketDetails?.hash,
        },
      }

      try {
        await patchDispatchTicket(dtUpdatePayload)
          .unwrap()
          .then(() => {
            if (outboundTicketData) {
              window.location.reload()
            }
          })
          .catch(error => {
            let errorMessage = 'There was an error linking the Dispatch Ticket.'

            if (error?.data?.code === GS_ERROR_CODE.CONCURRENCY_VIOLATION) {
              errorMessage = `Transportation Information updated successfully, but the Dispatch ticket has been updated by another user and couldn't be linked. Please refresh your page and try again.`
            }

            dispatchSnackbar(dispatch, 'error', errorMessage)
          })
      } catch (err) {
        dispatchSnackbar(
          dispatch,
          'error',
          'We could not set the dispatch ticket to completed. Outbound Ticket created successfully.',
        )
      }
    }
    updateDispatchTicket()
  }, [obtCreateSuccess, obtUpdateSuccess])

  const handleCreateObtConfirmed = () => {
    setUserDidConfirm(true)
  }

  return {
    handleCreateObtConfirmed,
  }
}

export default useLinkDispatchToObt
